<template>
  <div>
    <div class="page-card">
      <div class="section-head">
        <h2 class="page-title" style="margin-bottom: 0;">历史菜单</h2>
        <div class="history-actions">
          <el-button :loading="importBusy" @click="triggerImport">导入</el-button>
          <el-button type="primary" :loading="exportBusy" @click="onExport">导出</el-button>
        </div>
      </div>
      <input
        ref="importInput"
        type="file"
        multiple
        accept=".pdf,.txt,.text,.md,.csv,.docx,.doc,.rtf,.wps,.zip"
        style="display: none;"
        @change="onImportFiles"
      >
      <p v-if="!items.length" class="history-empty">暂无历史菜单，可通过导入或在「自动配菜」确认菜单后查看。</p>
      <div v-else class="history-row">
        <button
          v-for="item in items"
          :key="item.id"
          type="button"
          class="history-card"
          @click="openPreview(item.id)"
        >
          <span class="history-card-label">人均餐标</span>
          <span class="history-card-value">{{ formatMoney(item.perCapita) }}</span>
          <span class="history-card-label history-card-label-gap">就餐人数</span>
          <span class="history-card-count">{{ item.guestCount || '-' }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { addMenuHistory, listMenuHistory } from '@/utils/menuHistory'
import { parseHistoryFileName } from '@/utils/historyFileName'
import { expandImportFiles, isHistoryImportFile, readHistoryFileContent } from '@/utils/historyImport'
import { exportAllHistoryMenus } from '@/utils/historyExport'

export default {
  name: 'HistoryMenus',
  data() {
    return {
      items: [],
      importBusy: false,
      exportBusy: false
    }
  },
  created() {
    this.refresh()
  },
  activated() {
    this.refresh()
  },
  methods: {
    refresh() {
      this.items = listMenuHistory()
    },
    formatMoney(n) {
      return Number(n).toFixed(2)
    },
    openPreview(id) {
      this.$router.push({ name: 'MenuPreview', params: { id } })
    },
    triggerImport() {
      if (this.importBusy) return
      this.$refs.importInput.click()
    },
    async onImportFiles(event) {
      const files = await expandImportFiles(Array.from((event.target && event.target.files) || []))
      event.target.value = ''
      if (!files.length) return
      this.importBusy = true
      let added = 0
      let replaced = 0
      const errors = []
      const keys = []
      try {
        for (let i = 0; i < files.length; i += 1) {
          const file = files[i]
          if (!isHistoryImportFile(file.name)) {
            errors.push(`${file.name}：不支持的文件类型`)
            continue
          }
          const parsed = parseHistoryFileName(file.name)
          if (!parsed) {
            errors.push(`${file.name}：文件名须为「人均餐标-人数」，例如 300-8`)
            continue
          }
          try {
            const previewContent = await readHistoryFileContent(file)
            if (!previewContent) {
              errors.push(`${file.name}：未能识别文件内容`)
              continue
            }
            const before = listMenuHistory()
            const existed = before.some((row) => (
              Number(row.perCapita) === parsed.perCapita
              && Number(row.guestCount) === parsed.guestCount
            ))
            addMenuHistory({
              perCapita: parsed.perCapita,
              guestCount: parsed.guestCount,
              dishNames: previewContent.split(/\r?\n/).map((line) => line.trim()).filter(Boolean),
              dishes: [],
              previewContent,
              title: `${parsed.perCapita}-${parsed.guestCount}`
            })
            if (existed) replaced += 1
            else added += 1
            keys.push(`${parsed.perCapita}-${parsed.guestCount}`)
          } catch (e) {
            errors.push(`${file.name}：${e.message || '导入失败'}`)
          }
        }
        this.refresh()
        if (added || replaced) {
          const parts = []
          if (added) parts.push(`新增 ${added} 份`)
          if (replaced) parts.push(`替换 ${replaced} 份`)
          const keyText = keys.length ? `（${keys.join('、')}）` : ''
          this.$message.success(parts.join('，') + keyText)
        }
        if (errors.length) {
          this.$message.warning(errors.slice(0, 5).join('；') + (errors.length > 5 ? '…' : ''))
        }
        if (!added && !replaced && !errors.length) {
          this.$message.warning('没有可导入的文件')
        }
      } finally {
        this.importBusy = false
      }
    },
    async onExport() {
      if (this.exportBusy) return
      if (!this.items.length) {
        this.$message.warning('暂无历史菜单可导出')
        return
      }
      this.exportBusy = true
      try {
        await exportAllHistoryMenus()
        this.$message.success('已打包导出全部历史菜单')
      } catch (e) {
        this.$message.error(e.message || '导出失败')
      } finally {
        this.exportBusy = false
      }
    }
  }
}
</script>

<style scoped>
.history-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.history-empty {
  margin: 0;
  color: #909399;
}

.history-row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  padding: 8px 4px 16px;
}

.history-card {
  flex: 0 0 160px;
  width: 160px;
  height: 160px;
  margin: 0;
  padding: 16px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #f4f7fb 100%);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.06), 0 10px 20px rgba(15, 35, 80, 0.12);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #303133;
}

.history-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.08), 0 14px 24px rgba(15, 35, 80, 0.16);
}

.history-card:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.history-card-label {
  font-size: 13px;
  color: #909399;
  margin-bottom: 4px;
}

.history-card-label-gap {
  margin-top: 12px;
}

.history-card-value {
  font-size: 22px;
  font-weight: 600;
  color: #409eff;
}

.history-card-count {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}
</style>
